import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { applyPhotoDraft, PhotoError, type PhotoDraft } from './patient-photo';

export interface CardImage {
  blob: Blob;
  previewUrl: string;
}

export interface InsuranceDraft {
  enabled: boolean;
  payerId: string;
  planId: string;
  policyNo: string;
  validFrom: string;
  validTo: string;
  card: CardImage | null;
}

export const NO_INSURANCE: InsuranceDraft = { enabled: false, payerId: '', planId: '', policyNo: '', validFrom: '', validTo: '', card: null };

export type ExtraKind = 'photo' | 'insurance' | 'card';

export interface ExtraResult {
  kind: ExtraKind;
  ok: boolean;
  error: string | null;
}

export interface RegistrationExtras {
  photo: PhotoDraft;
  insurance: InsuranceDraft;
}

export type InsuranceProblem = 'insurance_payer_needed' | 'insurance_number_needed' | 'insurance_dates_reversed';

export function insuranceProblems(draft: InsuranceDraft): InsuranceProblem[] {
  if (!draft.enabled) return [];
  const problems: InsuranceProblem[] = [];
  if (!draft.payerId) problems.push('insurance_payer_needed');
  if (!draft.policyNo.trim()) problems.push('insurance_number_needed');
  if (draft.validFrom && draft.validTo && draft.validTo < draft.validFrom) problems.push('insurance_dates_reversed');
  return problems;
}

export function plannedExtras(extras: RegistrationExtras): ExtraKind[] {
  const kinds: ExtraKind[] = [];
  if (extras.photo.kind === 'new') kinds.push('photo');
  if (extras.insurance.enabled && extras.insurance.payerId && extras.insurance.policyNo.trim()) {
    kinds.push('insurance');
    if (extras.insurance.card) kinds.push('card');
  }
  return kinds;
}

function isoDay(day: string): string | null {
  return day ? new Date(`${day}T00:00:00.000Z`).toISOString() : null;
}

export function coverageBody(patientId: string, draft: InsuranceDraft) {
  return {
    patientId,
    payerId: draft.payerId,
    planId: draft.planId || null,
    policyNo: draft.policyNo.trim(),
    relation: 'self',
    validFrom: isoDay(draft.validFrom),
    validTo: isoDay(draft.validTo),
    isActive: true,
  };
}

interface PresignedUpload {
  key: string;
  url: string;
  method: string;
  headers?: Record<string, string>;
}

async function attachCard(patientId: string, card: CardImage): Promise<void> {
  const presigned = await post<PresignedUpload>('/files/presign-upload', {
    folder: 'patient-documents',
    filename: 'insurance-card.jpg',
    contentType: 'image/jpeg',
    sizeBytes: card.blob.size,
  });
  const response = await fetch(presigned.url, { method: presigned.method, headers: presigned.headers, body: card.blob });
  if (!response.ok) throw new PhotoError('photo_upload_failed');
  await post(`/patients/${patientId}/documents`, {
    title: tr('opd', 'insurance_card_document_title'),
    kind: 'insurance',
    fileKey: presigned.key,
    mimeType: 'image/jpeg',
    sizeBytes: card.blob.size,
  });
}

async function runStep(patientId: string, kind: ExtraKind, extras: RegistrationExtras): Promise<void> {
  if (kind === 'photo') return applyPhotoDraft(patientId, extras.photo);
  if (kind === 'insurance') {
    await post('/insurance/coverages', coverageBody(patientId, extras.insurance));
    return;
  }
  if (extras.insurance.card) await attachCard(patientId, extras.insurance.card);
}

export async function runExtra(patientId: string, kind: ExtraKind, extras: RegistrationExtras): Promise<ExtraResult> {
  try {
    await runStep(patientId, kind, extras);
    return { kind, ok: true, error: null };
  } catch (error) {
    return { kind, ok: false, error: error instanceof PhotoError ? tr('opd', error.problem) : describeError(error) };
  }
}

export async function runExtras(patientId: string, extras: RegistrationExtras, kinds: readonly ExtraKind[] = plannedExtras(extras)): Promise<ExtraResult[]> {
  const results: ExtraResult[] = [];
  for (const kind of kinds) results.push(await runExtra(patientId, kind, extras));
  return results;
}

export function withRetried(results: readonly ExtraResult[], retried: ExtraResult): ExtraResult[] {
  return results.map((result) => (result.kind === retried.kind ? retried : result));
}
